import Decorator from './decorator'
import ChargeDecorator from './charge'
import JobDecorator from './job'
import LocationDecorator from './location'
import accounting from 'accounting'
import moment from 'moment'
import { isObject, join, map } from 'lodash'

export default class LoadDecorator extends Decorator {
  /**
   * Commodity and location.
   *
   * @return string
   */
  commodityAndLocation () {
    const hasCommodity = isObject(this.commodity)
    const hasLocationFrom = isObject(this.location_from)
    const hasLocationTo = isObject(this.location_to)

    let response = []
    if (this.decorated.size) {
      response.push(`${this.decorated.size}t`)
    }

    if (hasCommodity) {
      response.push(this.commodity.name)
    }

    if (hasLocationFrom) {
      response.push(hasCommodity ? 'from' : 'From')
      response.push(this.location_from.formattedName)
    }

    if (hasLocationTo) {
      response.push(hasCommodity || hasLocationFrom ? 'to' : 'To')
      response.push(this.location_to.formattedName)
    }

    return join(response, ' ')
  }

  /**
   * The status text corresponding to each status.
   *
   * @return string
   */
  statusText () {
    if (this.status === 'scheduled') {
      return 'Scheduled'
    } else if (this.status === 'in_progress') {
      return 'In Progress'
    } else if (this.status === 'en_route') {
      return 'En Route'
    } else if (this.status === 'delivered') {
      return 'Delivered'
    } else if (this.status === 'picked_up') {
      return 'Picked Up'
    } else if (this.status === 'confirmed') {
      return 'Confirmed'
    } else if (this.status === 'cancelled') {
      return 'Cancelled'
    } else if (this.status === 'withdrawn') {
      return 'Withdrawn'
    }
  }

  /**
   * The status class corrosponding to each status.
   *
   * @return string
   */
  statusClass () {
    if (this.status === 'cancelled' || this.status === 'withdrawn') {
      return 'text-danger'
    }

    return 'text-primary'
  }

  /**
   * The delivery date.
   *
   * @return string
   */
  deliveryDate () {
    if (!this.decorated.delivery_date) {
      return '–'
    }

    return this.decorated.delivery_date
  }

  /**
   * The delivery time.
   *
   * @return string
   */
  deliveryTime () {
    if (!this.decorated.delivery_time) {
      return '–'
    }

    return this.decorated.delivery_time
  }

  /**
   * The loading date.
   *
   * @return string
   */
  loadingDate () {
    if (!this.decorated.loading_date) {
      return '–'
    }

    return this.decorated.loading_date
  }

  /**
   * The loading time.
   *
   * @return string
   */
  loadingTime () {
    if (!this.decorated.loading_time) {
      return '–'
    }

    return this.decorated.loading_time
  }

  /**
   * (User) Company Name
   */
  company () {
    if (isObject(this.carrier)) {
      return this.decorated.carrier.profile.company_name
    }

    return '–'
  }

  /**
   * Get the rate formatted as money.
   *
   * @return string
   */
  rate () {
    if (!this.decorated.rate) {
      return '–'
    }

    return accounting.formatMoney(this.decorated.rate)
  }

  /**
   * Estimated size of the load.
   *
   * @return string
   */
  size () {
    if (!this.decorated.size) {
      return '–'
    }

    return `${this.decorated.size}t`
  }

  /**
   * Get the net weight off of the loads weighbridge.
   *
   * @return string
   */
  netWeight () {
    if (!this.decorated.weighbridge || this.decorated.weighbridge.status !== 'approved') {
      return '–'
    }

    return `${this.decorated.weighbridge.total_tonnage}t`
  }

  /**
   * Truck registration.
   *
   * @return string
   */
  registration () {
    if (isObject(this.truck)) {
      return this.truck.registration
    }

    return '–'
  }

  /**
   * Truck configuration description.
   *
   * @return string
   */
  configurationDescription () {
    if (isObject(this.truckConfiguration)) {
      return `${this.truckConfiguration.net_weight}t ${this.truckConfiguration.description}`
    }

    return '–'
  }

  /**
   * Driver name.
   *
   * @return string
   */
  driverName () {
    if (isObject(this.driver)) {
      return `${this.driver.profile.name} ${this.driver.profile.surname}`
    }

    return '–'
  }

  /**
   * Commodity name.
   *
   * @return string
   */
  commodityName () {
    if (isObject(this.commodity)) {
      return this.commodity.name
    }

    return '–'
  }

  /**
   * The loading dates day in lowercase.
   *
   * @return string
   */
  day () {
    if (!this.decorated.loading_date) {
      return null
    }

    return moment(this.decorated.loading_date, 'DD/MM/YYYY').format('dddd').toLowerCase()
  }

  /**
   * Decorated job.
   *
   * @return object
   */
  job () {
    return new JobDecorator(this.decorated.job)
  }

  /**
   * Decorated location from.
   *
   * @return object
   */
  locationFrom () {
    if (isObject(this.decorated.location_from)) {
      return new LocationDecorator(this.decorated.location_from)
    } else if (isObject(this.decorated.job.location_from)) {
      return new LocationDecorator(this.decorated.job.location_from)
    }

    return {}
  }

  /**
   * Decorated location to.
   *
   * @return object
   */
  locationTo () {
    if (isObject(this.decorated.location_to)) {
      return new LocationDecorator(this.decorated.location_to)
    } else if (isObject(this.decorated.job.location_to)) {
      return new LocationDecorator(this.decorated.job.location_to)
    }

    return {}
  }

  /**
   * Decorated charges.
   *
   * @return array
   */
  charges () {
    return map(this.decorated.charges, charge => new ChargeDecorator(charge))
  }

  /**
   * Determine if the load is planned.
   *
   * @return boolean
   */
  isPlanned () {
    return this.decorated.id !== null && this.decorated.status === 'planned'
  }

  /**
   * Determine if the load is scheduled.
   *
   * @return boolean
   */
  isScheduled () {
    return this.decorated.status === 'scheduled'
  }

  /**
   * Determine if the load is in progress.
   *
   * @return boolean
   */
  isInProgress () {
    return this.decorated.status === 'in_progress'
  }

  /**
   * The status text corrosponding to each status.
   *
   * @return string
   */
  weighbridgeStatusText () {
    if (isObject(this.decorated.weighbridge)) {
      if (this.decorated.weighbridge.status === 'pending') {
        return 'Pending'
      } else if (this.decorated.weighbridge.status === 'awaiting_approval') {
        return 'Awaiting-Approval'
      } else if (this.decorated.weighbridge.status === 'part_approved') {
        return 'Part-Approved'
      } else if (this.decorated.weighbridge.status === 'resubmit') {
        return 'Resubmit'
      } else if (this.decorated.weighbridge.status === 'approved') {
        return 'Approved'
      }
    }

    return '–'
  }

  /**
   * The status class corrosponding to each status.
   *
   * @return string
   */
  weighbridgeStatusClass () {
    if (isObject(this.decorated.weighbridge)) {
      if (this.decorated.weighbridge.status === 'pending') {
        return 'text-primary'
      } else if (this.decorated.weighbridge.status === 'part_approved') {
        return 'text-info'
      } else if (this.decorated.weighbridge.status === 'resubmit') {
        return 'text-danger'
      } else if (this.decorated.weighbridge.status === 'approved') {
        return 'text-info'
      }
    }

    return 'text-primary'
  }
}



// WEBPACK FOOTER //
// ./src/decorators/load.js